import React, { useState, useRef, useEffect } from "react";

import { useHistory } from "react-router";
import { Trans, useTranslation } from "react-i18next";

import ImageHandler from "../../utils/ImagesHandler";
import UserProfileSchema from "../../schemas/userProfie.schema";
import UsersAPI from "../../API/users.api";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import LazyImage from "../Shared/LazyImage";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import { fetchLoggedUser } from "../../utils/UserHelper";

const UserProfileForm = ({ user, setEditMode }) => {
    const [currentInput, setCurrentInput] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});

    const uploadImageRef = useRef(null);
    const history = useHistory();
    const { t } = useTranslation();

    useEffect(() => {
        setCurrentInput({
            firstname: user.firstname,
            lastname: user.lastname,
            image: user.image,
            id: user.id,
        });
    }, [user]);

    useEffect(() => {
        validate(
            currentInput,
            UserProfileSchema.UserProfileSchema,
            setIsFormValid,
            setError
        );
    }, [currentInput]);

    const handleImageDelete = () => {
        setCurrentInput({ ...currentInput, image: null });
        uploadImageRef.current.value = "";
    };

    const handleFileClick = () => {
        uploadImageRef.current.click();
    };

    const handleUploadImage = async (e) => {
        ImageHandler.formatImage(e, (image) => {
            setCurrentInput({
                ...currentInput,
                image,
            });
        });
    };

    const handleInputChange = (event) => {
        setCurrentInput({
            ...currentInput,
            [event.target.name]: event.target.value,
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        try {
            const result = await UsersAPI.updateUser(currentInput);
            if (result) {
                history.push("/profile");
                setEditMode(false);
                SuccessAlert(t("editUserSuccessMessage"));
                LogCapture.info(
                    `User profile edited with name: ${currentInput?.firstname} ${currentInput?.lastname} & id: ${currentInput?.id}`
                );
                fetchLoggedUser();
            }
        } catch (e) {
            ErrorAlert(e);
            LogCapture.error(
                `Couldn't edit user profile with name: ${currentInput?.firstname} ${currentInput?.lastname} & id: ${currentInput?.id}, error: ${e}`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="card-table shadow">
                <div className="upload-img-box flex">
                    <div className="relative">
                        {currentInput.image && (
                            <span
                                className="img-delete-icon"
                                onClick={handleImageDelete}
                            >
                                <i className="far fa-times-circle" />
                            </span>
                        )}
                        <div
                            className="choose-img-wrapper"
                            onClick={handleFileClick}
                        >
                            <LazyImage
                                src={
                                    currentInput.image ||
                                    require("../../assets/images/no-avatar.png")
                                        .default
                                }
                                alt="Upload avatar"
                            />
                            <input
                                ref={uploadImageRef}
                                type="file"
                                name="image"
                                accept="image/*"
                                className="hidden"
                                onChange={handleUploadImage}
                            />

                            <div className="choose-img-btn flex">
                                <i className="fas fa-plus-square"></i>
                                <span className="ml-2 mt-2">
                                    <Trans i18nKey="upload">Upload</Trans>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="w-full">
                        <h3 className="medium-title-bold">
                            <Trans i18nKey="editPersonalInformation">
                                Edit Personal Information
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        <div
                            className={`tags input-group ${
                                error.firstname && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="firstName">Firstname</Trans>
                            </h4>
                            <input
                                type="text"
                                name="firstname"
                                value={currentInput.firstname || ""}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="Firstname"
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.firstname}
                                />
                            )}
                        </div>
                        <div
                            className={`tags input-group ${
                                error.lastname && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="lastName">Lastname</Trans>
                            </h4>
                            <input
                                type="text"
                                name="lastname"
                                value={currentInput.lastname || ""}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="Lastname"
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.lastname}
                                />
                            )}
                        </div>
                        <div className="main-customer-form-bnt flex items-center justify-end float-right mt-5">
                            <button
                                onClick={() => {
                                    setEditMode(false);
                                }}
                                className="secondary-blue-btn mr-2"
                            >
                                <Trans i18nKey="cancel">Cancel</Trans>
                            </button>
                            <button
                                onClick={handleSubmit}
                                className={`default-orange-outline-btn ${
                                    isSubmitting ? "disabled" : ""
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin mr-3 inline-flex">
                                            <i className="fas fa-sync"></i>
                                        </span>
                                        <Trans i18nKey="requesting">
                                            Requesting...
                                        </Trans>
                                    </>
                                ) : (
                                    <Trans i18nKey="edit">Edit</Trans>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserProfileForm;
